/**
 * JS-Port von /app/backend/routes/sizing_profile.py.
 *
 * Garantiert, dass Frag HANS und der Konfigurator-Wizard bei identischer
 * Eingabe IDENTISCHE Faktoren und Speicher-Empfehlungen liefern. Beide
 * Implementierungen müssen gemeinsam gepflegt werden, bis die geplante
 * Wissens-Editor-Phase die Tabellen in eine geteilte JSON migriert.
 *
 * Tests: /app/frontend/src/lib/__tests__ (oder per E2E-Test) — Werte müssen
 * 1:1 mit `from routes.sizing_profile import recommend(...)` übereinstimmen.
 */

// ─── §6 Basisfaktoren kWh/kWp ─────────────────────────────────────────
// Schlüssel-Format: `${profileClass}|${workTime || 'none'}`
// Variablen sind `let`, damit `loadLiveRules()` sie zur Laufzeit ersetzen
// kann (Rule-Editor im Dashboard). Die Default-Tabellen sind in
// `_DEFAULT_*` als Snapshot gesichert.
let BASE_FACTORS = {
  // Privat (workTime irrelevant → key endet auf '|none')
  'private_efh|none':       1.10,
  'private_villa|none':     1.275,
  'private_mfh|none':       0.60,
  // Büro / Praxis / Verwaltung
  'office|single_shift':    0.60,
  'office|extended':        0.85,
  'office|none':            0.60,
  // Werkstatt / Handwerk
  'workshop|single_shift':  0.80,
  'workshop|extended':      1.00,
  'workshop|none':          0.80,
  // Produktion
  'production|single_shift':0.70,
  'production|extended':    1.00,
  'production|continuous':  0.80,
  'production|none':        0.80,
  // Landwirtschaft
  'agri_livestock|none':    1.20,
  'agri_general|none':      1.00,
  // Handel / Einzelhandel
  'retail|none':            0.75,
  // Gastronomie / Hotel / Café
  'gastro|single_shift':    0.85,
  'gastro|evening':         1.30,
  'gastro|continuous':      1.20,
  'gastro|none':            1.10,
};
const _DEFAULT_BASE_FACTORS = { ...BASE_FACTORS };

// ─── §7 Uplifts ───────────────────────────────────────────────────────
let UPLIFTS = {
  backup:         0.15,
  villa_extra:    0.15,
  evening:        0.20,
  weekend:        0.10,
  cooling:        0.15,
  ventilation:    0.15,
  heatpump:       0.20,
  high_autarky:   0.25,
  critical_loads: 0.15,
};
const _DEFAULT_UPLIFTS = { ...UPLIFTS };

// ─── §9 Min/Max-Grenzen ───────────────────────────────────────────────
let LIMITS = {
  private_efh:           [0.7, 1.6],
  private_efh_backup:    [1.1, 1.6],
  private_villa:         [1.2, 1.45],
  private_villa_backup:  [1.25, 1.60],
  private_mfh:           [0.4, 1.3],
  office:                [0.4, 0.8],
  office_extended:       [0.7, 1.0],
  workshop:              [0.6, 1.2],
  production:            [0.5, 1.2],
  retail:                [0.5, 1.2],
  gastro:                [1.0, 1.5],
  agri_livestock:        [1.0, 1.4],
  agri_general:          [0.8, 1.2],
};
const _DEFAULT_LIMITS = JSON.parse(JSON.stringify(LIMITS));

// Live-Sync mit Backend-Rule-Editor — wird einmalig beim App-Bootstrap
// aufgerufen (siehe index.js). Bei Erfolg ersetzen die Live-Werte aus
// /api/sizing-rules/public die hardcodierten Defaults — ohne dass
// `recommendFactor` oder `classifyFromWizardData` etwas davon mitbekommen.
export async function loadLiveRules(apiBase) {
  try {
    const url = (apiBase || process.env.REACT_APP_BACKEND_URL || '') + '/api/sizing-rules/public';
    const r = await fetch(url, { method: 'GET' });
    if (!r.ok) return false;
    const d = await r.json();
    if (Array.isArray(d.baseFactors)) {
      const next = {};
      for (const e of d.baseFactors) {
        const wt = e.workTime || 'none';
        next[`${e.profileClass}|${wt}`] = Number(e.value);
      }
      BASE_FACTORS = next;
    }
    if (d.uplifts && typeof d.uplifts === 'object') {
      const next = {};
      for (const [k, v] of Object.entries(d.uplifts)) next[k] = Number(v);
      UPLIFTS = next;
    }
    if (d.limits && typeof d.limits === 'object') {
      const next = {};
      for (const [k, v] of Object.entries(d.limits)) {
        if (Array.isArray(v) && v.length === 2) next[k] = [Number(v[0]), Number(v[1])];
      }
      LIMITS = next;
    }
    return true;
  } catch {
    return false;
  }
}

// Defensiv: erlaubt Tests / Stories die Tabellen wieder auf Werks-Defaults
// zu setzen, ohne das Backend zu konsultieren.
export function resetLiveRulesToDefault() {
  BASE_FACTORS = { ..._DEFAULT_BASE_FACTORS };
  UPLIFTS = { ..._DEFAULT_UPLIFTS };
  LIMITS = JSON.parse(JSON.stringify(_DEFAULT_LIMITS));
}

function _baseFactor(profileClass, workTime) {
  if (!profileClass) return BASE_FACTORS['private_efh|none'];
  const wt = workTime || 'none';
  const key = `${profileClass}|${wt}`;
  if (key in BASE_FACTORS) return BASE_FACTORS[key];
  const noneKey = `${profileClass}|none`;
  if (noneKey in BASE_FACTORS) return BASE_FACTORS[noneKey];
  return BASE_FACTORS['private_efh|none'];
}

function _limits(profileClass, wantsBackup, workTime) {
  if (!profileClass) return LIMITS.private_efh;
  if (wantsBackup && LIMITS[`${profileClass}_backup`]) return LIMITS[`${profileClass}_backup`];
  if (workTime === 'extended' && LIMITS[`${profileClass}_extended`]) return LIMITS[`${profileClass}_extended`];
  return LIMITS[profileClass] || LIMITS.private_efh;
}

/**
 * §6–§9: Rule-Engine für PV-kWp-basierte Speicher-Empfehlung.
 *
 * @param {object} args
 * @param {number} args.pvKwp                     PV-Leistung in kWp.
 * @param {string} args.profileClass              z.B. 'private_villa'.
 * @param {string|null} args.workTime             optional 'single_shift', 'evening', …
 * @param {object} args.flags                     {backup, evening, weekend, cooling, …}
 *                                                booleans.
 * @returns {{baseFactor:number, finalFactor:number, usableKwh:number,
 *            appliedUplifts:object, limits:[number,number]}}
 */
export function recommendFactor({ pvKwp, profileClass, workTime, flags = {} }) {
  const pv = Number(pvKwp) || 0;
  const cls = profileClass || 'private_efh';
  const base = _baseFactor(cls, workTime);

  const applied = {};
  for (const [flag, uplift] of Object.entries(UPLIFTS)) {
    if (flag === 'villa_extra') continue;       // wird nur extern entschieden
    // Wenn der Uplift bereits durch workTime abgebildet wird, nicht doppelt.
    if (flag === 'evening' && workTime === 'evening') continue;
    if (flag === 'weekend' && workTime === 'weekend') continue;
    if (flags[flag]) applied[flag] = uplift;
  }
  const raw = base + Object.values(applied).reduce((a, b) => a + b, 0);
  const [minF, maxF] = _limits(cls, !!flags.backup, workTime);
  const finalF = Math.max(minF, Math.min(maxF, raw));
  const usable = Math.round(finalF * pv * 100) / 100;
  return {
    baseFactor: Math.round(base * 1000) / 1000,
    rawFactor:  Math.round(raw * 1000) / 1000,
    finalFactor: Math.round(finalF * 1000) / 1000,
    appliedUplifts: applied,
    usableKwh: usable,
    limits: [minF, maxF],
  };
}

/**
 * Leitet aus den (strukturierten) Wizard-Eingaben die Profilklasse, Arbeitszeit
 * und Flags ab — gleicher Output wie sizing_profile.classify_profile().
 */
export function classifyFromWizardData(data) {
  const customerType = data.customerType;
  const persons = Number(data.persons || 0);
  const industry = (data.industry || '').toLowerCase();
  const shift = (data.shiftType || '').toLowerCase();
  const backupNeedClass = data.backupNeedClass;

  // Profilklasse
  let profileClass;
  if (customerType === 'business') {
    if (industry.includes('büro') || industry.includes('praxis') || industry.includes('office')) profileClass = 'office';
    else if (industry.includes('werkstatt') || industry.includes('handwerk') || industry.includes('kfz')) profileClass = 'workshop';
    else if (industry.includes('produktion') || industry.includes('fertigung') || industry.includes('industrie')) profileClass = 'production';
    else if (industry.includes('handel') || industry.includes('einzelhandel') || industry.includes('supermarkt')) profileClass = 'retail';
    else if (industry.includes('gastro') || industry.includes('restaurant') || industry.includes('hotel') || industry.includes('café') || industry.includes('cafe')) profileClass = 'gastro';
    else if (industry.includes('tier') || industry.includes('milch') || industry.includes('melk')) profileClass = 'agri_livestock';
    else if (industry.includes('land') || industry.includes('hof') || industry.includes('agri')) profileClass = 'agri_general';
    else profileClass = 'office';   // fallback
  } else {
    profileClass = persons >= 5 ? 'private_villa' : 'private_efh';
  }

  // Arbeitszeit
  let workTime = null;
  if (shift === 'continuous' || shift.includes('24/7') || shift.includes('dauer')) workTime = 'continuous';
  else if (shift.includes('zwei') || shift.includes('extended')) workTime = 'extended';
  else if (shift.includes('abend') || shift.includes('evening')) workTime = 'evening';
  else if (shift.includes('wochen') || shift.includes('weekend')) workTime = 'weekend';
  else if (shift.includes('saison') || shift.includes('seasonal')) workTime = 'seasonal';
  else if (customerType === 'business') workTime = 'single_shift';

  // Flags (analog UPLIFT_KEYWORDS aus Python)
  const flags = {
    backup:         backupNeedClass && backupNeedClass !== 'none',
    cooling:        false,   // im Wizard aktuell nicht abgefragt
    ventilation:    false,
    heatpump:       !!data.hasHeatPump,
    high_autarky:   false,
    critical_loads: false,
    wallbox:        !!data.hasEv,
  };
  return { profileClass, workTime, flags };
}
